import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { MessageRow } from './MessageRow';
import { useConversationStream } from './useConversationStream';
import { conversations as api, type Conversation } from '../../api/communication';
import { m } from 'motion/react';
import { ChevronLeft, MessagesSquare } from 'lucide-react';

import { LiveDot } from '../../components/Badge';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { fieldStyles } from '../../components/styles';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { useT } from '../../i18n/provider';
import { cn } from '../../lib/cn';
import { useCompanyId } from '../company/useCompany';

/**
 * The conversation viewer of `06 §7` and `13`.
 *
 * Two columns, exactly as the specification draws them: the threads on the left
 * with their recency and their participants, the messages on the right — and
 * **the blocked attempts inline in the thread they aimed at**, not in a separate
 * error list. `06 §7` is explicit about why: *«Es información de gobierno, no un
 * error a esconder»*. A manager reading a thread needs to see the question
 * somebody was not allowed to ask, in the place where it would have been asked.
 *
 * From a blocked attempt the policy that caused it is one click away. That link
 * is the reason the endpoint attaches the whole decision to the message rather
 * than only a reason code: the screen must be able to say *which* rule, and take
 * the manager to it.
 */
export function ConversationsScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const client = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState('');

  const threads = useQuery({
    queryKey: ['conversations', companyId, status],
    queryFn: () => api.list(companyId, { status }),
    enabled: companyId !== '',
  });

  const detail = useQuery({
    queryKey: ['conversation', selected],
    queryFn: () => api.show(selected ?? ''),
    enabled: selected !== null,
  });

  const messages = useQuery({
    queryKey: ['conversation-messages', selected],
    queryFn: () => api.messages(selected ?? ''),
    enabled: selected !== null,
  });

  const archive = useMutation({
    mutationFn: () => api.archive(selected ?? ''),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['conversations', companyId] });
      void client.invalidateQueries({ queryKey: ['conversation', selected] });
    },
  });

  /**
   * A live frame refetches rather than being spliced into the list.
   *
   * Splicing would need this component to rebuild a `Message` out of a thinner
   * payload — inventing the fields the frame does not carry — and the one that
   * matters most on a blocked attempt is the policy decision. A refetch of a
   * hundred rows is cheap; a message drawn from a half-built object is a screen
   * that disagrees with the database.
   */
  const onFrame = useCallback(() => {
    void client.invalidateQueries({ queryKey: ['conversation-messages', selected] });
    void client.invalidateQueries({ queryKey: ['conversations', companyId] });
  }, [client, companyId, selected]);

  const { connected } = useConversationStream(companyId, selected, onFrame);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('conversations.title')}
        description={t('conversations.intro')}
        actions={
          <span className="flex shrink-0 items-center gap-2 text-xs text-ink-soft">
            <LiveDot tone={connected ? 'success' : 'neutral'} live={connected} />
            {connected ? t('conversations.live') : t('conversations.offline')}
          </span>
        }
      />

      {/*
        Maestro-detalle por debajo de `lg`.

        Las dos columnas conviven cuando hay sitio, pero apiladas la lista
        entera de hilos quedaba encima del hilo abierto: elegir uno te obligaba
        a recorrer toda la lista otra vez para llegar a los mensajes. Con la
        pantalla estrecha se ve una cosa o la otra, y el hilo trae su propio
        «volver».

        No hace falta ni un hook de tamaño ni un `matchMedia`: la selección ya
        es estado de esta pantalla, así que lo que se ve es cuestión de una
        clase.
      */}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <aside className={cn('space-y-3', selected !== null && 'hidden lg:block')}>
          <select
            aria-label={t('conversations.status')}
            className={cn(fieldStyles, 'border-line')}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{t('conversations.allThreads')}</option>
            <option value="open">{t('conversations.open')}</option>
            <option value="resolved">{t('conversations.resolved')}</option>
            <option value="archived">{t('conversations.archived')}</option>
          </select>

          {threads.isLoading ? <SkeletonRows rows={4} /> : null}

          {threads.data?.data.length === 0 ? (
            <EmptyState
              icon={<MessagesSquare />}
              tone="muted"
              title={t('conversations.empty')}
              description={t('conversations.emptyHint')}
            />
          ) : null}

          <ul className="space-y-1">
            {threads.data?.data.map((thread) => (
              <li key={thread.id}>
                <button
                  type="button"
                  onClick={() => setSelected(thread.id)}
                  className={cn(
                    'w-full rounded-sm border px-3 py-2.5 text-left text-sm transition-colors duration-fast',
                    selected === thread.id
                      ? 'border-primary/35 bg-primary-pale text-ink'
                      : 'border-transparent text-ink-soft hover:bg-muted hover:text-ink',
                  )}
                >
                  <ThreadRow thread={thread} />
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className={cn('min-w-0', selected === null && 'hidden lg:block')}>
          {selected === null ? (
            <EmptyState
              icon={<MessagesSquare />}
              tone="muted"
              title={t('conversations.pickThread')}
              description={t('conversations.pickThreadHint')}
            />
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="-ml-1 flex min-h-11 items-center gap-1.5 text-sm font-semibold text-ink-soft transition-colors hover:text-ink lg:hidden"
              >
                <ChevronLeft className="size-4" aria-hidden="true" />
                {t('conversations.backToThreads')}
              </button>

              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line pb-3">
                <div>
                  <h3 className="text-base font-semibold text-ink">{detail.data?.topic ?? '…'}</h3>
                  <p className="font-mono text-xs text-ink-faint">
                    {detail.data?.participants.map((p) => p.slug).join(' · ') ?? ''}
                    {detail.data?.task_number == null
                      ? ''
                      : ` · ${t('conversations.task', { number: detail.data.task_number })}`}
                  </p>
                </div>
                <Button
                  variant="secondary"
                  disabled={archive.isPending || detail.data?.status === 'archived'}
                  onClick={() => archive.mutate()}
                >
                  {detail.data?.status === 'archived' ? t('conversations.archivedState') : t('conversations.archive')}
                </Button>
              </div>

              {messages.isLoading ? <SkeletonRows rows={5} /> : null}

              {/* `aria-live` en el hilo: los mensajes llegan por Mercure y
                  quien usa lector de pantalla tiene que enterarse. */}
              <m.ol aria-live="polite" variants={STAGGER} initial="hidden" animate="shown" className="space-y-4">
                {messages.data?.data.map((message) => (
                  <m.li key={message.id} variants={RISE}>
                    <MessageRow message={message} />
                  </m.li>
                ))}
              </m.ol>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ThreadRow({ thread }: { thread: Conversation }) {
  const t = useT();

  return (
    <>
      <span className="block truncate font-semibold">{thread.topic}</span>
      <span className="block text-xs text-ink-faint">
        {t('conversations.messages', { count: thread.message_count })}
        {thread.status === 'archived' ? t('conversations.archivedSuffix') : ''}
        {thread.scope === 'inter_company' ? t('conversations.interCompany') : ''}
      </span>
    </>
  );
}
